import React, { useState, useEffect } from 'react';
import { useChatbotContext } from '../../../context/ChatbotContext';
import ChatbotSection from './ChatbotSection/ChatbotSection';
import ChatbotBuilder from './ChatbotBuilder/ChatbotBuilder';

import OpenAIFiles from './ChatbotBuilder/KnowledgeBase/OpenAI/OpenAIFiles';
import WebsiteTab from './ChatbotBuilder/KnowledgeBase/Website/WebsiteTab';
import Dashboard from './ChatbotBuilder/Analytics/Dashboard/Dashboard';
import Walkthroughs from './ChatbotBuilder/Walkthroughs/Walkthroughs';
import { v4 as uuidv4 } from 'uuid';
import './Chatbot.css';

function Chatbot({ selectedChatbotId, setSelectedChatbotId, activeSubTab, setActiveSubTab }) {
  const { loading, fetchChatbotConfig, resetToDefaultConfig, chatbots } = useChatbotContext();
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [announcement, setAnnouncement] = useState({
    text: '',
    enabled: false,
    style: 'info'
  });

  // Load config when selectedChatbotId changes
  useEffect(() => {
    if (selectedChatbotId && !loading) {
      loadChatbotConfig(selectedChatbotId);
    } else if (!selectedChatbotId) {
      setConfig(null);
      setIsPublished(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatbotId, loading, chatbots]);


  const loadChatbotConfig = async (id) => {
    setConfigLoading(true);

    // First, try to fetch the config from backend
    // This will return the config if it exists, or null if it doesn't
    const fetchedConfig = await fetchChatbotConfig(id);

    if (fetchedConfig) {
      // Config exists in backend - this is an existing chatbot
      setIsPublished(true);
      // Ensure announcement exists in config with defaults if not present
      fetchedConfig.announcement = fetchedConfig.announcement || {
        text: '',
        enabled: false,
        style: 'info'
      };
      setConfig(fetchedConfig);
    } else {
      // No config found - check if it's in the chatbots list to determine if it's new or existing
      const isExistingChatbot = chatbots.length > 0 && chatbots.some(cb => cb.chatbot_id === id);

      if (isExistingChatbot) {
        // It's in the list but config fetch returned null - this shouldn't happen normally
        // But handle it gracefully - maybe retry once
        setTimeout(async () => {
          const retryConfig = await fetchChatbotConfig(id);
          if (retryConfig) {
            retryConfig.announcement = retryConfig.announcement || {
              text: '',
              enabled: false,
              style: 'info'
            };
            setConfig(retryConfig);
            setIsPublished(true);
          }
        }, 500);
        setIsPublished(true);
        // Keep existing config or null state
      } else {
        // Not in chatbots list - this is a new chatbot (or chatbots haven't loaded yet)
        // Use default config
        setIsPublished(false);
        const defaultConfig = resetToDefaultConfig();
        setConfig(defaultConfig);
      }
    }
    setConfigLoading(false);
  };

  const handleEditChatbot = async (id) => {
    localStorage.setItem('chatbotId', id);
    setSelectedChatbotId(id);
    setActiveSubTab('Identity'); // Start with Identity (Design tab)
    localStorage.setItem('activeSubTab', 'Identity');
  };

  const handleAddNewChatbot = async () => {
    setIsPublished(false);
    setConfigLoading(true);
    const newChatbotId = uuidv4();
    localStorage.setItem('chatbotId', newChatbotId);

    // Get default config (which now includes announcement)
    const defaultConfig = resetToDefaultConfig();

    setConfig(defaultConfig);
    setSelectedChatbotId(newChatbotId);
    setActiveSubTab('Profile'); // Start with Profile
    localStorage.setItem('activeSubTab', 'Profile');
    setConfigLoading(false);
  };

  // Loading State - Minimal Skeleton
  if (loading || configLoading) {
    return (
      <div className="h-full flex flex-col bg-slate-50/50 animate-in fade-in duration-500">
        {/* Header Skeleton */}
        <div className="px-8 pt-8 pb-6 border-b border-slate-200/40 bg-white/40 backdrop-blur-sm">
          <div className="flex justify-between items-end mb-6">
            <div className="space-y-2">
              <div className="h-8 w-48 bg-slate-200/70 rounded-lg animate-pulse"></div>
              <div className="h-4 w-64 bg-slate-200/50 rounded-md animate-pulse"></div>
            </div>
            <div className="h-10 w-32 bg-slate-200/70 rounded-xl animate-pulse"></div>
          </div>
          <div className="flex gap-4 opacity-60">
            <div className="h-11 flex-1 bg-slate-200/50 rounded-xl animate-pulse"></div>
            <div className="flex gap-1 bg-slate-200/50 p-1 rounded-lg animate-pulse w-24"></div>
          </div>
        </div>

        {/* Grid Skeleton */}
        <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-[240px] bg-white border border-slate-100 rounded-xl p-5 shadow-sm flex flex-col">
              <div className="flex justify-between mb-4">
                <div className="flex gap-3 w-full">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 animate-pulse shrink-0"></div>
                  <div className="space-y-2 w-full">
                    <div className="h-5 w-3/4 bg-slate-100 rounded animate-pulse"></div>
                    <div className="h-3 w-1/3 bg-slate-50 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
              <div className="flex-1 w-full bg-slate-50/50 rounded-lg animate-pulse mb-4 border border-slate-50"></div>
              <div className="grid grid-cols-3 gap-2">
                <div className="h-8 bg-slate-100 rounded-md animate-pulse"></div>
                <div className="h-8 bg-slate-100 rounded-md animate-pulse"></div>
                <div className="h-8 bg-slate-100 rounded-md animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render different content based on activeSubTab
  const renderContent = () => {
    if (!selectedChatbotId) {
      return (
        <ChatbotSection
          onEditChatbot={handleEditChatbot}
          onAddNewChatbot={handleAddNewChatbot}
        />
      );
    }

    // For builder-related tabs (Profile, Theme, etc.), show ChatbotBuilder
    // Map new sidebar names to internal component names
    const subTabMapping = {
      'General': 'Advance',
      'Integrations': 'Integration'
    };

    const effectiveSubTab = subTabMapping[activeSubTab] || activeSubTab;

    const builderTabs = ['Profile', 'Identity', 'Theme', 'Toggle Button', 'ProactiveBubble', 'HumanHandoff', 'Advance', 'Integration', 'Publish', 'Chat Menu', 'Files', 'Forms'];

    // Provide an empty mock config block structure if somehow config is null during mount
    // to prevent the builder canvas from permanently rendering blank (returning null at fallback)
    const activeConfig = config || {};

    if (builderTabs.includes(effectiveSubTab)) {
      return (
        <ChatbotBuilder
          chatbot={activeConfig}
          onClose={() => {
            setSelectedChatbotId(null);
            setConfig(null);
            localStorage.removeItem('chatbotId');
            localStorage.removeItem('activeSubTab');
          }}
          isPublished={isPublished}
          announcement={announcement}
          setAnnouncement={setAnnouncement}
          activeSubTab={effectiveSubTab}
          setActiveSubTab={(tab) => {
            // Reverse mapping if needed, or just set the internal name
            // If ChatbotBuilder sets 'Profile', we might want to set 'Identity' in parent?
            // For now, let's keep it simple. If ChatbotBuilder sets a tab, it's likely internal navigation.
            setActiveSubTab(tab);
          }}
        />
      );
    }

    // For Dashboard - show Dashboard component
    if (activeSubTab === 'Dashboard' || activeSubTab === 'Analytics') {
      return <Dashboard selectedChatbotId={selectedChatbotId} />;
    }



    if (activeSubTab === 'OpenAI') {
      return <OpenAIFiles selectedChatbotId={selectedChatbotId} />;
    }

    if (activeSubTab === 'Website') {
      return <WebsiteTab selectedChatbotId={selectedChatbotId} />;
    }



    if (activeSubTab === 'Walkthroughs') {
      return <Walkthroughs selectedChatbotId={selectedChatbotId} />;
    }

    // Default fallback
    return null;
  };

  return renderContent();
}

export default Chatbot;